import { useApolloClient, useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useIntl } from 'react-intl';
import { POLICYTYPE } from '~/modules/common/enums';
import { prependNoneItem } from '~/modules/common/enhancers/prependNoneItem';
import { PAGE_SIZE } from '~/modules/common/const';

export const EMPLOYEETYPE_QUERY = gql`
  query getEmployeeTypes(
    $searchTerm: String
    $policy: String
    $pageSize: Int
    $scope: ScopeObjectType
  ) {
    employeeTypes(
      searchTerm: $searchTerm
      page: 1
      pageSize: $pageSize
      policy: $policy
      scope: $scope
    ) {
      id
      displayText
      hierarchyLevel
    }
  }
`;

const mappedEmployeeTypes = data =>
  data?.employeeTypes?.map(employeeType => ({
    ...employeeType,
    value: employeeType.id,
    label: employeeType.displayText,
    key: employeeType.displayText
  }));

const useEmployeeTypes = ({
  searchTerm,
  policy = POLICYTYPE.PROJECT_MANAGEMENT,
  includeNoneOption = false,
  noneOptionText = '',
  scope
}) => {
  const { data, loading, refetch } = useQuery(EMPLOYEETYPE_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      searchTerm,
      pageSize: PAGE_SIZE + 1,
      policy,
      scope
    },
    context: {
      debounceKey: 'employee-type-search',
      debounceTimeout: 250
    }
  });

  const employeeTypes = !loading ? mappedEmployeeTypes(data) : [];

  return {
    employeeTypes: (employeeTypes.length && includeNoneOption
      ? prependNoneItem(noneOptionText, employeeTypes)
      : employeeTypes
    ).slice(0, includeNoneOption ? PAGE_SIZE + 1 : PAGE_SIZE),
    loading,
    refetch,
    hasMore: employeeTypes.length > PAGE_SIZE
  };
};

export const fetchEmployeeTypes = ({
  apolloClient,
  policy = POLICYTYPE.PROJECT_MANAGEMENT,
  intl
}) => async searchTerm => {
  const { data } = await apolloClient.query({
    query: EMPLOYEETYPE_QUERY,
    variables: { searchTerm, pageSize: PAGE_SIZE, policy }
  });

  return prependNoneItem(
    intl.formatMessage({ id: 'resourceBasicInfoCard.noEmployeeType' }),
    mappedEmployeeTypes(data)
  );
};

export const useSearchableEmployeeTypes = ({ policy } = {}) => {
  const apolloClient = useApolloClient();
  const intl = useIntl();

  const fetchOptions = fetchEmployeeTypes({
    apolloClient,
    policy,
    intl
  });

  return { fetchOptions };
};

export default useEmployeeTypes;
